import { EVENTS } from '../../constants';

/**
 * The SocialShare module which handles social media sharing functionality.
 * @class SocialShare
 */
export default class SocialShare {
  /**
   * SocialShare constructor which assigns the element passed into the constructor
   * to the `this.element` property for later reference
   * @param {HTMLElement} element - REQUIRED - the module's container
   * @param {object} services - The services object for dependency injection
   */
  constructor(element, services) {
    /**
     * Reference to the DOM element that is the root of the component
     * @property {object} element - The DOM element that serves as the root of the component.
     */
    this.element = element;

    /**
     * Reference to the services object
     * @property {object} services - The services object for dependency injection.
     */
    this.services = services;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners
   * @returns {object} SocialShare A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} SocialShare A reference to the instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.socialLinks = this.element.querySelectorAll('.banner__social-link');
    this.emailSubject = this.element.querySelector('[data-email-subject]');
    this.emailBody = this.element.querySelector('[data-email-body]');

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.onSocialClick = this.onSocialClick.bind(this);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} SocialShare A reference to the instance of the class
   * @chainable
   */
  enable() {
    this.socialLinks.forEach((link) => {
      link.addEventListener(EVENTS.CLICK, this.onSocialClick);
    });

    return this;
  }

  /**
   * Handle social media link clicks
   * @param {Event} event - The click event
   * @returns {object} A reference to the current instance of the class
   */
  onSocialClick(event) {
    event.preventDefault();

    const { currentTarget: link } = event;
    const { social: socialType } = link.dataset;
    const url = window.location.href;
    const { title } = document;

    let shareUrl = '';

    switch (socialType) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'email':
        this.handleEmailShare();
        return this;
      default:
        return this;
    }

    if (shareUrl) {
      window.open(
        shareUrl,
        '_blank',
        'width=600,height=400,scrollbars=yes,resizable=yes'
      );
    }

    return this;
  }

  /**
   * Handle email sharing
   * @returns {object} A reference to the current instance of the class
   */
  handleEmailShare() {
    const subject = this.emailSubject
      ? this.emailSubject.dataset.emailSubject
      : document.title;
    const body = this.emailBody
      ? this.emailBody.dataset.emailBody
      : window.location.href;

    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;

    return this;
  }
}
